'use client'

import hoursData from '#/content/hours.json'
import { useCmsContent } from '#/hooks/useCmsContent'
import type { BusinessHours } from '#/lib/content-types'
import { Link } from '@tanstack/react-router'
import { Clock, Mail, MapPin, Phone } from 'lucide-react'
import Container from './Container'

const quickLinks = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/menu', label: 'Menu' },
  { to: '/services', label: 'Services' },
  { to: '/blog', label: 'Blog' },
  { to: '/contact', label: 'Contact' },
] as const

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// Group consecutive days with identical hours into compact ranges,
// e.g. "Tue–Wed: 8:00 AM – 4:00 PM" instead of two separate lines.
function groupHours(hours: BusinessHours[]) {
  const sorted = [...hours].sort((a, b) => a.dayOfWeek - b.dayOfWeek)
  const groups: Array<{ start: number; end: number; closed: boolean; open: string; close: string }> = []
  for (const h of sorted) {
    const last = groups[groups.length - 1]
    const sameAsLast =
      last &&
      last.end === h.dayOfWeek - 1 &&
      last.closed === h.closed &&
      last.open === h.open &&
      last.close === h.close
    if (sameAsLast) {
      last.end = h.dayOfWeek
    } else {
      groups.push({ start: h.dayOfWeek, end: h.dayOfWeek, closed: h.closed, open: h.open, close: h.close })
    }
  }
  return groups.map((g) => ({
    label: g.start === g.end ? DAY_ABBR[g.start] : `${DAY_ABBR[g.start]}–${DAY_ABBR[g.end]}`,
    value: g.closed ? 'Closed' : `${g.open} – ${g.close}`,
  }))
}

export default function Footer() {
  const year = new Date().getFullYear()
  const hours = useCmsContent('hours', hoursData as BusinessHours[])
  const groupedHours = groupHours(hours)

  return (
    <footer className="relative bg-gradient-to-b from-surface-high to-surface-highest py-12 sm:py-16">
      {/* Top accent */}
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary via-primary-container to-tertiary" />

      <Container>
        <div className="grid gap-10 md:grid-cols-3">
          {/* Business info */}
          <div>
            <img
              src="/images/logo.svg"
              alt="Main Street Bread"
              className="mb-4 h-8 w-auto"
            />
            <ul className="space-y-3 text-sm text-on-surface-variant">
              <li className="flex items-start gap-2">
                <MapPin className="mt-0.5 size-4 shrink-0" />
                <span>2054 Main St, Ferndale, WA 98248</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="size-4 shrink-0" />
                <span>(360) 306-5563</span>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="size-4 shrink-0" />
                <span>mainstreetbread@gmail.com</span>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="mt-0.5 size-4 shrink-0" />
                <div className="space-y-0.5">
                  {groupedHours.map((g) => (
                    <div key={g.label}>
                      <span className="font-medium">{g.label}:</span> {g.value}
                    </div>
                  ))}
                </div>
              </li>
            </ul>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="mb-4 font-serif text-lg font-bold text-on-surface">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-on-surface-variant no-underline transition-colors hover:text-primary"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Follow Us */}
          <div>
            <h3 className="mb-4 font-serif text-lg font-bold text-on-surface">
              Follow Us
            </h3>
            <p className="mb-4 text-sm leading-relaxed text-on-surface-variant">
              Catch seasonal specials, community events, and what&apos;s fresh from the oven over on social.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/main_street_bread"
                target="_blank"
                rel="noreferrer"
                className="text-on-surface-variant transition-colors hover:text-primary"
                aria-label="Instagram"
              >
                <svg viewBox="0 0 24 24" className="size-6 transition-transform hover:scale-110" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61586804853756#"
                target="_blank"
                rel="noreferrer"
                className="text-on-surface-variant transition-colors hover:text-primary"
                aria-label="Facebook"
              >
                <svg viewBox="0 0 24 24" className="size-6 transition-transform hover:scale-110" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385h-3.047v-3.47h3.047v-2.642c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953h-1.514c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385c5.737-.9 10.125-5.864 10.125-11.854z" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-10 pt-6 text-center">
          <div className="mx-auto mb-6 h-px w-full bg-gradient-to-r from-transparent via-primary-container/30 to-transparent" />
          <p className="text-xs text-on-surface-variant">
            &copy; {year} Main Street Bread. All rights reserved.
          </p>
        </div>
      </Container>
    </footer>
  )
}
